/**
 * Client-side mock execution for workflow nodes.
 * Generates realistic fake output per node type/config and infers
 * a detailed outputSchema from the result.
 */

function titleCase(str) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function generateMockOutput(nodeType, config = {}) {
  switch (nodeType) {
    case 'searchCRM': {
      if (config.recordType === 'person') {
        return {
          results: [
            { id: 'per_001', first_name: 'Sarah', last_name: 'Chen', email: 'sarah.chen@acme.com', phone: '+1-555-0101', title: 'VP of Engineering' },
            { id: 'per_002', first_name: 'Marcus', last_name: 'Rivera', email: 'marcus@globex.io', phone: '+1-555-0102', title: 'Head of Product' },
            { id: 'per_003', first_name: 'Aisha', last_name: 'Patel', email: 'aisha.patel@initech.com', phone: '+1-555-0103', title: 'CEO' },
          ],
          count: 3,
        };
      }
      return {
        results: [
          { id: 'comp_001', name: 'Acme Corp', industry: 'Technology', country: 'United States', website: 'https://acme.com', phone: '+1-555-1000', status: 'active' },
          { id: 'comp_002', name: 'Globex International', industry: 'Manufacturing', country: 'Germany', website: 'https://globex.io', phone: '+49-30-12345', status: 'qualified' },
          { id: 'comp_003', name: 'Initech Solutions', industry: 'SaaS', country: 'Denmark', website: 'https://initech.com', phone: '+45-70-123456', status: 'active' },
        ],
        count: 3,
      };
    }

    case 'createCompany':
      return {
        record: { id: 'comp_new_001', name: config.name || 'New Company', industry: 'Technology', country: 'United States', status: 'active', created_at: new Date().toISOString() },
      };

    case 'getCompany':
      return {
        record: { id: config.companyId || 'comp_001', name: 'Acme Corp', industry: 'Technology', country: 'United States', website: 'https://acme.com', phone: '+1-555-1000', status: 'active' },
      };

    case 'updateCompany':
      return {
        record: { id: config.companyId || 'comp_001', name: 'Acme Corp', industry: 'Technology', country: 'United States', website: 'https://acme.com', phone: '+1-555-1000', status: 'active' },
      };

    case 'createPerson':
      return {
        record: { id: 'per_new_001', first_name: config.firstName || 'New', last_name: config.lastName || 'Person', email: 'new.person@example.com', title: 'Employee', created_at: new Date().toISOString() },
      };

    case 'getPerson':
      return {
        record: { id: config.personId || 'per_001', first_name: 'Sarah', last_name: 'Chen', email: 'sarah.chen@acme.com', phone: '+1-555-0101', title: 'VP of Engineering' },
      };

    case 'updatePerson':
      return {
        record: { id: config.personId || 'per_001', first_name: 'Sarah', last_name: 'Chen', email: 'sarah.chen@acme.com', phone: '+1-555-0101', title: 'VP of Engineering' },
      };

    case 'createDeal':
      return {
        record: { id: 'deal_new_001', name: config.name || 'New Deal', value: config.value || 50000, stage: 'Prospecting', created_at: new Date().toISOString() },
      };

    case 'getDeal':
      return {
        record: { id: config.dealId || 'deal_001', name: 'Enterprise License', value: 75000, stage: 'Negotiation', company_id: 'comp_001', owner_id: 'user_001' },
      };

    case 'updateDeal':
      return {
        record: { id: config.dealId || 'deal_001', name: 'Enterprise License', value: 75000, stage: 'Negotiation', company_id: 'comp_001', owner_id: 'user_001' },
      };

    case 'createRecord':
      return {
        record: { id: 'rec_new_001', object_definition_id: config.objectDefinitionId || 'objdef_001', data: config.fieldValues || { name: 'New Record' }, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      };

    case 'getRecord':
      return {
        record: { id: config.recordId || 'rec_001', object_definition_id: config.objectDefinitionId || 'objdef_001', data: { name: 'Sample Record', email: 'sample@example.com', status: 'active' }, created_at: '2026-01-15T10:00:00Z', updated_at: '2026-03-10T14:30:00Z' },
      };

    case 'updateRecord':
      return {
        record: { id: config.recordId || 'rec_001', object_definition_id: config.objectDefinitionId || 'objdef_001', data: { name: 'Updated Record', ...(config.fieldValues || {}) }, created_at: '2026-01-15T10:00:00Z', updated_at: new Date().toISOString() },
      };

    case 'deleteRecord':
      return {
        success: true,
        recordId: config.recordId || 'rec_001',
      };

    case 'searchRecords':
      return {
        results: [
          { id: 'rec_001', object_definition_id: config.objectDefinitionId || 'objdef_001', data: { name: 'Acme Corp', status: 'active' }, created_at: '2026-01-15T10:00:00Z', updated_at: '2026-03-10T14:30:00Z' },
          { id: 'rec_002', object_definition_id: config.objectDefinitionId || 'objdef_001', data: { name: 'Globex International', status: 'qualified' }, created_at: '2026-02-01T09:00:00Z', updated_at: '2026-03-08T11:00:00Z' },
        ],
        count: 2,
        total: 2,
      };

    case 'callLLM':
      return {
        output: 'Based on the data provided, here are the key findings: The portfolio shows strong growth in Q4 with a 23% increase in qualified leads. Recommend focusing outreach on the technology and SaaS verticals.',
        model: config.model || 'claude-sonnet',
      };

    case 'fetchWebsite':
      return {
        body: '<!DOCTYPE html><html><head><title>Example</title></head><body><h1>Hello World</h1><p>Page content here...</p></body></html>',
        statusCode: 200,
        headers: { 'content-type': 'text/html', 'x-request-id': 'req_abc123', 'cache-control': 'max-age=3600' },
      };

    case 'getWebsiteContent':
      return {
        markdown: '# Example Domain\n\nThis domain is for use in illustrative examples in documents.\n\n[More information...](https://www.iana.org/domains/example)',
        url: config.url || 'https://example.com',
        success: true,
      };

    case 'linkedinPersonFinder': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const output = {
        profiles: [
          {
            fullName: `${config.firstName || 'Sarah'} ${config.lastName || 'Chen'}`,
            firstName: config.firstName || 'Sarah',
            lastName: config.lastName || 'Chen',
            headline: 'VP of Engineering at Acme Corp',
            currentTitle: 'VP of Engineering',
            currentCompany: 'Acme Corp',
            location: 'San Francisco Bay Area',
            linkedinUrl: 'https://www.linkedin.com/in/sarahchen',
            email: config.profileScraperMode === 'Full + email search' ? 'sarah.chen@example.com' : '',
            about: 'Engineering leader focused on data products and automation.',
            experience: [
              { title: 'VP of Engineering', companyName: 'Acme Corp' },
            ],
            education: [
              { schoolName: 'Stanford University', degreeName: 'BS Computer Science' },
            ],
          },
        ],
        count: 1,
        search: {
          firstName: config.firstName || 'Sarah',
          lastName: config.lastName || 'Chen',
          profileScraperMode: config.profileScraperMode || 'Short',
          strictSearch: config.strictSearch !== false,
          maxPages: config.maxPages || 1,
          maxItems: config.maxItems || 5,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = [
          {
            fullName: `${config.firstName || 'Sarah'} ${config.lastName || 'Chen'}`,
            profileUrl: 'https://www.linkedin.com/in/sarahchen',
          },
        ];
      }

      return output;
    }

    case 'linkedinCompanyEmployees': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const output = {
        profiles: [
          {
            fullName: 'Alex Rivera',
            firstName: 'Alex',
            lastName: 'Rivera',
            headline: 'Senior Product Manager at Acme Corp',
            currentTitle: 'Senior Product Manager',
            currentCompany: 'Acme Corp',
            location: 'New York, United States',
            linkedinUrl: 'https://www.linkedin.com/in/alexrivera',
            email: config.profileScraperMode === 'Full + email search ($12 per 1k)' ? 'alex.rivera@acme.com' : '',
          },
        ],
        count: 1,
        search: {
          companyUrls: config.companyUrls || 'https://www.linkedin.com/company/acme',
          profileScraperMode: config.profileScraperMode || 'Short ($4 per 1k)',
          maxItems: config.maxItems || 25,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = [
          {
            fullName: 'Alex Rivera',
            linkedinUrl: 'https://www.linkedin.com/in/alexrivera',
            currentPosition: [{ companyName: 'Acme Corp' }],
          },
        ];
      }

      return output;
    }

    case 'googleSearchResults': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const includePeopleAlsoAsk = config.includePeopleAlsoAsk !== false && config.includePeopleAlsoAsk !== 'false';
      const includeAds = config.includeAds === true || config.includeAds === 'true';
      const includeAiOverview = config.includeAiOverview === true || config.includeAiOverview === 'true';
      const queryList = String(config.queries || 'best crm for startups')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const primaryQuery = queryList[0] || 'best crm for startups';

      const output = {
        results: [
          {
            query: primaryQuery,
            rank: 1,
            title: 'Top CRM Software for Startups in 2026',
            url: 'https://example.com/crm-for-startups',
            displayUrl: 'example.com/crm-for-startups',
            snippet: 'Compare CRM options for early-stage teams, including pricing and features.',
            type: 'organic',
          },
          {
            query: primaryQuery,
            rank: 2,
            title: 'Startup CRM Buyer Guide',
            url: 'https://example.org/startup-crm-guide',
            displayUrl: 'example.org/startup-crm-guide',
            snippet: 'A practical guide for choosing and implementing a CRM for growth teams.',
            type: 'organic',
          },
        ],
        questions: includePeopleAlsoAsk
          ? [
            'What CRM is best for small startups?',
            'How much should a startup spend on CRM?',
          ]
          : [],
        ads: includeAds
          ? [
            {
              query: primaryQuery,
              rank: 1,
              title: 'CRM for Startups - Free Trial',
              url: 'https://ads.example.net/startup-crm',
              displayUrl: 'ads.example.net/startup-crm',
              snippet: 'Launch your sales pipeline in minutes.',
              type: 'ad',
            },
          ]
          : [],
        count: 2,
        search: {
          queries: queryList,
          countryCode: String(config.countryCode || 'US').trim().toUpperCase() || 'US',
          languageCode: String(config.languageCode || 'en').trim().toLowerCase() || 'en',
          maxPagesPerQuery: Number.isFinite(Number(config.maxPagesPerQuery))
            ? Math.min(Math.max(Math.floor(Number(config.maxPagesPerQuery)), 1), 3)
            : 1,
          includePeopleAlsoAsk,
          includeAds,
          includeAiOverview,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = [
          {
            searchQuery: primaryQuery,
            page: 1,
            organicResults: [
              {
                position: 1,
                title: 'Top CRM Software for Startups in 2026',
                url: 'https://example.com/crm-for-startups',
              },
            ],
          },
        ];
      }

      return output;
    }

    case 'linkedinJobsSearch': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const searchTerms = String(config.searchTerms || 'product manager')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const locations = String(config.locations || '')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const companyUrls = String(config.companyUrls || '')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const maxItems = Number.isFinite(Number(config.maxItems))
        ? Math.min(Math.max(Math.floor(Number(config.maxItems)), 1), 100)
        : 25;
      const primaryTerm = searchTerms[0] || 'product manager';
      const primaryLocation = locations[0] || 'Copenhagen, Denmark';
      const primaryCompanyUrl = companyUrls[0] || 'https://www.linkedin.com/company/acme';

      const jobs = [
        {
          title: `Senior ${primaryTerm}`,
          companyName: 'Acme Corp',
          companyUrl: primaryCompanyUrl,
          location: primaryLocation,
          jobUrl: 'https://www.linkedin.com/jobs/view/1234567890',
          postedAt: '2026-05-14T09:30:00.000Z',
          description: 'Own core growth experiments and partner with sales and product teams.',
          employmentType: 'Full-time',
        },
        {
          title: `${primaryTerm} - Demand Signals`,
          companyName: 'Globex',
          companyUrl: 'https://www.linkedin.com/company/globex',
          location: locations[1] || primaryLocation,
          jobUrl: 'https://www.linkedin.com/jobs/view/2234567890',
          postedAt: '2026-05-13T14:10:00.000Z',
          description: 'Build outbound signal pipelines and drive account prioritization.',
          employmentType: 'Contract',
        },
      ].slice(0, maxItems);

      const companies = Array.from(
        jobs.reduce((acc, job) => {
          const key = `${job.companyName}::${job.companyUrl}`;
          if (!acc.has(key)) {
            acc.set(key, {
              companyName: job.companyName,
              companyUrl: job.companyUrl,
            });
          }
          return acc;
        }, new Map())
          .values()
      );

      const output = {
        jobs,
        companies,
        count: jobs.length,
        search: {
          searchTerms,
          locations,
          companyUrls,
          maxItems,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = [
          {
            title: `Senior ${primaryTerm}`,
            companyName: 'Acme Corp',
            companyLink: primaryCompanyUrl,
            location: primaryLocation,
            jobPostingUrl: 'https://www.linkedin.com/jobs/view/1234567890',
          },
        ];
      }

      return output;
    }

    case 'googleMapsReviews': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const placeUrls = String(config.placeUrls || 'https://www.google.com/maps/place/acme')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const maxReviews = Number.isFinite(Number(config.maxReviews))
        ? Math.min(Math.max(Math.floor(Number(config.maxReviews)), 1), 200)
        : 25;
      const minRating = Number.isFinite(Number(config.minRating))
        ? Math.min(Math.max(Number(config.minRating), 1), 5)
        : null;
      const sort = String(config.sort || 'newest').trim() || 'newest';

      const baseReviews = [
        {
          placeName: 'Acme HQ',
          rating: 5,
          text: 'Great onboarding experience and very responsive support team.',
          publishedAt: '2026-05-01T12:00:00.000Z',
          reviewerName: 'Jamie Lee',
          reviewUrl: 'https://www.google.com/maps/reviews/data=sample-1',
          ownerResponse: 'Thanks for the feedback, Jamie!',
        },
        {
          placeName: 'Acme HQ',
          rating: 4,
          text: 'Solid service. Would love extended weekend support hours.',
          publishedAt: '2026-04-28T09:30:00.000Z',
          reviewerName: 'Chris Morgan',
          reviewUrl: 'https://www.google.com/maps/reviews/data=sample-2',
          ownerResponse: '',
        },
      ];

      const filteredReviews = baseReviews
        .filter((review) => minRating == null || review.rating >= minRating)
        .slice(0, maxReviews);
      const averageRating = filteredReviews.length
        ? Number(
          (filteredReviews.reduce((sum, review) => sum + review.rating, 0) / filteredReviews.length).toFixed(2)
        )
        : 0;

      const output = {
        reviews: filteredReviews,
        averageRating,
        count: filteredReviews.length,
        search: {
          placeUrls,
          maxReviews,
          sort,
          minRating,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = [
          {
            placeUrl: placeUrls[0] || 'https://www.google.com/maps/place/acme',
            reviewId: 'sample-1',
            reviewer: { name: 'Jamie Lee' },
            stars: 5,
            text: 'Great onboarding experience and very responsive support team.',
            ownerResponse: 'Thanks for the feedback, Jamie!',
          },
        ];
      }

      return output;
    }

    case 'facebookAdsLibrarySearch': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const searchTerms = String(config.searchTerms || 'nike')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const pageIds = String(config.pageIds || '')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const adLibraryUrls = String(config.adLibraryUrls || '')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean);
      const countries = String(config.countries || 'ALL')
        .split(/[\n,]/)
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);
      const activeStatus = ['active', 'inactive', 'all'].includes(String(config.activeStatus || '').trim())
        ? String(config.activeStatus).trim()
        : 'active';
      const maxItems = Number.isFinite(Number(config.maxItems))
        ? Math.min(Math.max(Math.floor(Number(config.maxItems)), 1), 100)
        : 25;
      const primarySearchTerm = searchTerms[0] || 'nike';
      const primaryPageId = pageIds[0] || '15087023444';

      const ads = [
        {
          adId: '3948271054812',
          advertiserName: 'Nike',
          adText: `Just do it - ${primarySearchTerm} spring collection`,
          landingPageUrl: 'https://www.nike.com',
          startedAt: '2026-05-01T00:00:00+0000',
          endedAt: activeStatus === 'inactive' ? '2026-05-10T00:00:00+0000' : '',
          isActive: activeStatus !== 'inactive',
          platforms: ['facebook', 'instagram'],
          imageUrls: ['https://images.example.com/nike-ad-1.jpg'],
          videoUrls: [],
          adSnapshotUrl: 'https://www.facebook.com/ads/archive/render_ad/?id=3948271054812',
        },
        {
          adId: '7261039485012',
          advertiserName: 'Acme Fitness',
          adText: `Train smarter with ${primarySearchTerm} routines`,
          landingPageUrl: 'https://acmefitness.example/offer',
          startedAt: '2026-04-22T00:00:00+0000',
          endedAt: '',
          isActive: true,
          platforms: ['facebook', 'instagram', 'audience_network'],
          imageUrls: ['https://images.example.com/acme-ad-1.jpg'],
          videoUrls: ['https://video.example.com/acme-ad-1.mp4'],
          adSnapshotUrl: 'https://www.facebook.com/ads/archive/render_ad/?id=7261039485012',
        },
      ].slice(0, maxItems);

      const output = {
        ads,
        count: ads.length,
        search: {
          searchTerms,
          pageIds,
          adLibraryUrls,
          countries: countries.length > 0 ? countries : ['ALL'],
          activeStatus,
          maxItems,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = [
          {
            adArchiveID: '3948271054812',
            pageID: primaryPageId,
            pageName: 'Nike',
            adText: `Just do it - ${primarySearchTerm} spring collection`,
            publisherPlatforms: ['facebook', 'instagram'],
            adStatus: activeStatus === 'inactive' ? 'INACTIVE' : 'ACTIVE',
            adSnapshotUrl: 'https://www.facebook.com/ads/archive/render_ad/?id=3948271054812',
            sourceUrl: adLibraryUrls[0]
              || `https://www.facebook.com/ads/library/?active_status=${activeStatus}&ad_type=all&country=${(countries[0] || 'ALL')}&q=${encodeURIComponent(primarySearchTerm)}&search_type=keyword_unordered`,
          },
        ];
      }

      return output;
    }

    case 'instagramProfileSnapshot': {
      const includeRawItems = config.includeRawItems === true || config.includeRawItems === 'true';
      const includeRecentPosts = config.includeRecentPosts !== false && config.includeRecentPosts !== 'false';
      const includeAboutSection = config.includeAboutSection === true || config.includeAboutSection === 'true';
      const maxItems = Number.isFinite(Number(config.maxItems))
        ? Math.min(Math.max(Math.floor(Number(config.maxItems)), 1), 100)
        : 25;
      const maxPosts = Number.isFinite(Number(config.maxPosts))
        ? Math.min(Math.max(Math.floor(Number(config.maxPosts)), 1), 25)
        : 6;
      const requestedProfiles = String(config.profileUrlsOrUsernames || 'natgeo')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, maxItems);

      const normalizedUsernames = requestedProfiles.length > 0
        ? requestedProfiles.map((value) => {
          const trimmed = value.trim();
          const urlMatch = trimmed.match(/instagram\.com\/([A-Za-z0-9._]+)/i);
          if (urlMatch?.[1]) return urlMatch[1].replace(/^@+/, '');
          return trimmed.replace(/^@+/, '').replace(/\/+$/, '');
        }).filter(Boolean)
        : ['natgeo'];

      const profiles = normalizedUsernames.map((username, index) => {
        const recentPosts = [
          {
            id: `${username}-post-1`,
            shortcode: 'Cxyz1234',
            caption: `Behind the scenes with @${username} and this week's feature.`,
            likesCount: 14203,
            commentsCount: 284,
            displayUrl: `https://images.example.com/${username}/post-1.jpg`,
            postUrl: `https://www.instagram.com/p/Cxyz1234/`,
            timestamp: '2026-05-12T10:30:00.000Z',
          },
          {
            id: `${username}-post-2`,
            shortcode: 'Cxyz5678',
            caption: 'New collection drop and story highlights.',
            likesCount: 9630,
            commentsCount: 121,
            displayUrl: `https://images.example.com/${username}/post-2.jpg`,
            postUrl: `https://www.instagram.com/p/Cxyz5678/`,
            timestamp: '2026-05-10T08:00:00.000Z',
          },
        ].slice(0, maxPosts);

        return {
          username,
          fullName: `${username.charAt(0).toUpperCase()}${username.slice(1)} Studio`,
          bio: 'Public profile focused on social storytelling, product updates, and behind-the-scenes content.',
          website: `https://example.com/${username}`,
          followersCount: 120000 - (index * 8500),
          followsCount: 430 + index,
          postsCount: 860 + (index * 11),
          isVerified: index % 2 === 0,
          profileUrl: `https://www.instagram.com/${username}/`,
          latestPosts: includeRecentPosts ? recentPosts : [],
          about: includeAboutSection
            ? {
              joinedAt: '2016-04-01',
              country: 'United States',
            }
            : undefined,
        };
      });

      const output = {
        profiles,
        count: profiles.length,
        search: {
          profileUrlsOrUsernames: requestedProfiles,
          usernames: normalizedUsernames,
          maxItems,
          includeAboutSection,
          includeRecentPosts,
          maxPosts,
          includeRawItems,
        },
      };

      if (includeRawItems) {
        output.rawItems = normalizedUsernames.map((username) => ({
          username,
          url: `https://www.instagram.com/${username}/`,
          biography: `Raw actor payload for ${username}`,
          followersCount: 120000,
        }));
      }

      return output;
    }

    case 'sendEmail':
      return { messageId: 'msg_abc123def456ghi789' };

    case 'iterate':
      return {
        items: [{ id: 'rec_001', name: 'Sample Record' }, { id: 'rec_002', name: 'Another Record' }],
        count: 2,
        executed: 2,
        results: [],
        iterations: [
          { index: 0, item: { id: 'rec_001', name: 'Sample Record' }, status: 'completed', outputs: {} },
          { index: 1, item: { id: 'rec_002', name: 'Another Record' }, status: 'completed', outputs: {} },
        ],
      };

    case 'workflowTrigger':
      return { payload: { workflowId: 'wf_abc123', triggeredAt: new Date().toISOString() } };

    case 'extractData': {
      const mode = config.mode || 'keyPath';
      if (mode === 'regex') {
        return { value: 'sarah.chen@acme.com', matched: true };
      }
      return { value: 'Sarah Chen', matched: true };
    }

    case 'parseJson':
      return {
        parsed: {
          name: 'Acme Corp',
          industry: 'Technology',
          employees: 150,
          active: true,
          tags: ['enterprise', 'saas'],
        },
      };

    case 'delay':
      return { resumedAt: new Date(Date.now() + 5 * 60 * 1000).toISOString() };

    case 'router':
      return {
        matchedBranch: (config.rules?.[0]?.label) || 'Route A',
        matchedIndex: 0,
      };

    case 'perplexityResearch':
      return {
        answer: 'Based on current research, the AI industry is experiencing rapid growth in 2026. Key trends include multi-modal AI models, AI agents in enterprise workflows, and advances in reasoning capabilities. The market is projected to reach $500B by 2027.',
        citations: ['https://example.com/ai-trends-2026', 'https://example.com/market-report', 'https://example.com/enterprise-ai'],
        model: config.model || 'sonar',
      };

    case 'webSearch':
      return {
        results: [
          { title: 'example.com', url: 'https://example.com/crm-reviews', snippet: 'The top CRM platforms of 2026 include Salesforce, HubSpot, and several emerging players. Key features to look for include AI-powered insights, workflow automation, and multi-channel communication...' },
          { title: 'techcrunch.com', url: 'https://techcrunch.com/crm-tools', snippet: '' },
          { title: 'gartner.com', url: 'https://gartner.com/crm-market', snippet: '' },
        ],
        resultCount: 3,
        summary: 'The best CRM tools in 2026 include Salesforce for enterprise, HubSpot for mid-market, and Pipedrive for small businesses. Key trends include AI-powered lead scoring, automated workflows, and integrated communication channels.',
      };

    case 'googlePlacesSearch':
      return {
        results: [
          { name: 'Nordic Dental Clinic', address: 'Vesterbrogade 45, 1620 København V, Denmark', phone: '+45 33 12 34 56', website: 'https://nordicdental.dk', rating: 4.7, placeId: 'ChIJ_abc123', types: ['dentist', 'health'], location: { lat: 55.6711, lng: 12.5577 } },
          { name: 'City Tandlæge', address: 'Strøget 12, 1150 København K, Denmark', phone: '+45 33 98 76 54', website: 'https://citytandlaege.dk', rating: 4.5, placeId: 'ChIJ_def456', types: ['dentist', 'health'], location: { lat: 55.6786, lng: 12.5733 } },
          { name: 'Frederiksberg Tandklinik', address: 'Gammel Kongevej 102, 1850 Frederiksberg, Denmark', phone: '+45 38 88 11 22', website: 'https://ftandklinik.dk', rating: 4.8, placeId: 'ChIJ_ghi789', types: ['dentist', 'health'], location: { lat: 55.6809, lng: 12.5263 } },
        ],
        resultCount: 3,
      };

    case 'conditional':
    case 'cron':
    case 'manual':
    case 'stop':
      return null;

    default:
      return null;
  }
}

export function inferSchema(data, prefix = '') {
  if (data == null) return [];

  const schema = [];

  for (const [key, value] of Object.entries(data)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      schema.push({ path, label: titleCase(key), type: 'array' });
      // Recurse into first element for array item fields
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        for (const [childKey, childVal] of Object.entries(value[0])) {
          schema.push({
            path: `${path}[].${childKey}`,
            label: titleCase(childKey),
            type: typeof childVal === 'number' ? 'number' : typeof childVal === 'boolean' ? 'boolean' : 'string',
          });
        }
      }
    } else if (typeof value === 'object' && value !== null) {
      schema.push({ path, label: titleCase(key), type: 'object' });
      // Recurse one level into nested objects
      for (const [childKey, childVal] of Object.entries(value)) {
        const childType = Array.isArray(childVal) ? 'array'
          : typeof childVal === 'number' ? 'number'
          : typeof childVal === 'boolean' ? 'boolean'
          : typeof childVal === 'object' && childVal !== null ? 'object'
          : 'string';
        schema.push({
          path: `${path}.${childKey}`,
          label: titleCase(childKey),
          type: childType,
        });
      }
    } else {
      schema.push({
        path,
        label: titleCase(key),
        type: typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'string',
      });
    }
  }

  return schema;
}
